document.addEventListener("DOMContentLoaded", async () => {
  // Configuration for grouped fee
  const GROUPED_FEE_CONFIG = window.GROUPED_FEE_CONFIG || {};

  // Flag to prevent infinite loops
  let isProcessing = false;
  let retryCount = 0;
  const MAX_RETRIES = 3;
  const MOBILE_DELAY = 500; // Additional delay for mobile devices

  // Detect if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  console.log(
    `%c [V3] Initializing on ${isMobile ? 'MOBILE' : 'DESKTOP'} device`,
    "background: #10b981; color: white; font-size: 14px; padding: 3px 12px; border-radius: 4px;"
  );

  // Function to wait for cart-items element to be ready
  async function waitForCartItemsElement(timeout = 5000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      function checkElement() {
        const cartItemsEl = document.querySelector('cart-items');
        
        if (cartItemsEl && typeof cartItemsEl.enableLoading === 'function') {
          console.log(
            "%c [V3] cart-items element is ready",
            "background: #10b981; color: white; font-size: 12px; padding: 2px 8px; border-radius: 3px;"
          );
          resolve(cartItemsEl);
          return;
        }
        
        if (Date.now() - startTime > timeout) {
          console.warn('[V3] Timeout waiting for cart-items element');
          resolve(null); // Don't reject, continue without element
          return;
        }
        
        setTimeout(checkElement, 100);
      }
      
      checkElement();
    });
  }

  // Function to validate GROUPED_FEE_CONFIG
  function validateConfig() {
    if (!GROUPED_FEE_CONFIG.variantId) {
      console.error('[V3] GROUPED_FEE_CONFIG.variantId is missing');
      return false;
    }
    
    console.log(
      "%c [V3] Config validated:",
      "background: #3b82f6; color: white; font-size: 12px; padding: 2px 8px; border-radius: 3px;"
    );
    console.log(GROUPED_FEE_CONFIG);
    return true;
  }

  const extractVariantIdFromGID = (gid) => {
    const match = gid.match(/gid:\/\/shopify\/ProductVariant\/(\d+)/);
    return match ? match[1] : gid;
  };

  // Optimized function - no more properties needed for grouped fee
  async function updateCartVariantQuantity(variantId, quantity, variantInCart) {
    if (quantity === 0) {
      return await removeCartVariant(variantId);
    }
    
    if (variantInCart?.[variantId]?.quantity > 0) {
      // Since we're not using properties anymore, we can just use change.js
      return await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity }),
      });
    } else {
      // Add new item without properties
      return await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
    }
  }

  async function removeCartVariant(variantId) {
    return await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 0 }),
    });
  }

  // New function to update cart with attributes and quantity updates in one call
  async function updateCartWithAttributes(updates, feeBreakdown, totalFeeAmount) {
    const cartAttributes = {
      'fee_total_amount': totalFeeAmount.toString(),
      'fee_breakdown': JSON.stringify(feeBreakdown),
      'fee_version': 'v3',
      'fee_variant_id': GROUPED_FEE_CONFIG.variantId
    };

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          attributes: cartAttributes,
          sections: document.querySelector('cart-items')?.getSectionsToRender().map(section => section.id)
        })
      });
      
      
      return await response.json();
    } catch (error) {
      console.error('Error updating cart with attributes:', error);
      throw error;
    }
  }

  // New function to calculate total fee amount (excluding crafting fees)
  function calculateTotalFeeAmount(cartItems) {
    let totalFeeAmount = 0; // Only for grouped fee (excluding crafting)
    let craftingFeeAmount = 0; // Separate tracking for crafting fees
    const feeBreakdown = {
      additionalFees: 0,
      craftingFees: 0,
      manPowerFees: 0,
      details: [],
      craftingFeeVariants: [] // Track crafting fee variants separately
    };

    // Calculate Additional Fees
    const additionalFeeAggregation = {};
    
    cartItems.forEach(item => {
      if (item.dataset.productAdditionalFee && item.dataset.productAdditionalFee !== "null") {
        const additionalFee = JSON.parse(item.dataset.productAdditionalFee);
        const feeVariantId = extractVariantIdFromGID(additionalFee.fee_product_variant);
        
        if (!additionalFeeAggregation[feeVariantId]) {
          additionalFeeAggregation[feeVariantId] = {
            totalProductQty: 0,
            packageQty: parseFloat(additionalFee.package_quantity),
            feePerStep: parseFloat(additionalFee.fee),
            cap: parseFloat(additionalFee.fee_cap),
          };
        }
        additionalFeeAggregation[feeVariantId].totalProductQty += parseInt(item.dataset.quantity || "0");
      }
    });

    for (const feeVariantId in additionalFeeAggregation) {
      const agg = additionalFeeAggregation[feeVariantId];
      const numPackages = Math.ceil(agg.totalProductQty / agg.packageQty);
      const feeAmount = Math.min(numPackages * agg.feePerStep, agg.cap);
      totalFeeAmount += feeAmount; // Add to grouped fee
      feeBreakdown.additionalFees += feeAmount;
      feeBreakdown.details.push({
        type: 'additional',
        variantId: feeVariantId,
        amount: feeAmount,
        packages: numPackages
      });
    }

    // Calculate Crafting Fees (separate from grouped fee)
    const craftingFeeMap = {};
    
    cartItems.forEach(item => {
      if (item.dataset.productCraftingFee && item.dataset.productCraftingFee !== "null") {
        const craftingFee = JSON.parse(item.dataset.productCraftingFee);
        const feeVariantId = extractVariantIdFromGID(craftingFee.fee_product_variant);

        if (!craftingFeeMap[feeVariantId]) {
          craftingFeeMap[feeVariantId] = {
            totalProductQty: 0,
            packageQty: parseFloat(craftingFee.package_quantity),
            feePerUnit: parseFloat(craftingFee.fee),
            cap: craftingFee.fee_cap ? parseFloat(craftingFee.fee_cap) : null,
          };
        }
        craftingFeeMap[feeVariantId].totalProductQty += parseInt(item.dataset.quantity || "0");
      }
    });

    for (const key in craftingFeeMap) {
      const { totalProductQty, packageQty, feePerUnit, cap } = craftingFeeMap[key];
      const expectedPackages = Math.ceil(totalProductQty / packageQty);
      const feeAmount = cap !== null ? Math.min(expectedPackages * feePerUnit, cap) : expectedPackages * feePerUnit;
      
      // DON'T add to totalFeeAmount (grouped fee), track separately
      craftingFeeAmount += feeAmount;
      feeBreakdown.craftingFees += feeAmount;
      feeBreakdown.details.push({
        type: 'crafting',
        variantId: key,
        amount: feeAmount,
        packages: expectedPackages
      });
      
      // Track crafting fee variants separately
      feeBreakdown.craftingFeeVariants.push({
        variantId: key,
        requiredQuantity: expectedPackages
      });
    }

    // Calculate Man-power Fees
    let manPowerFeeAmount = 0;
    const manPowerDebug = [];
    
    cartItems.forEach(item => {
      if (item.dataset.productManPower && item.dataset.productManPower !== "null") {
        try {
          const manPowerData = JSON.parse(item.dataset.productManPower);
          if (manPowerData && manPowerData.price) {
            // Convert from cents to dollars
            const manPowerPrice = parseFloat(manPowerData.price) / 100;
            manPowerFeeAmount = manPowerPrice;
            
            manPowerDebug.push({
              productName: item.dataset.productTitle || 'Unknown',
              originalPrice: manPowerData.price, // cents
              convertedPrice: manPowerPrice, // dollars
              data: manPowerData
            });
          }
        } catch (e) {
          console.error('Error parsing manPowerData:', e);
        }
      }
    });

    if (manPowerFeeAmount > 0) {
      totalFeeAmount += manPowerFeeAmount; // Add to grouped fee
      feeBreakdown.manPowerFees = manPowerFeeAmount;
      feeBreakdown.details.push({
        type: 'manpower',
        amount: manPowerFeeAmount,
        debug: manPowerDebug
      });
      
      console.log(
        "%c [V3] ManPower Fee Debug:",
        "background: #7c3aed; color: white; font-size: 14px; padding: 3px 12px; border-radius: 4px;"
      );
      console.log(manPowerDebug);
    }

    return { 
      totalFeeAmount, // Only additional + manpower fees for grouped fee
      craftingFeeAmount, // Separate crafting fee amount
      feeBreakdown 
    };
  }

  const onCartUpdate = async function (event) {
    // Set flag to prevent recursive calls
    let updateResponse = null;
    if (isProcessing) {
      return;
    }
    isProcessing = true;

    try {
      // Add mobile delay if needed
      if (isMobile && !event?.source) {
        await new Promise(resolve => setTimeout(resolve, MOBILE_DELAY));
      }

      const cartItemsEl = document.querySelector('cart-items');
      if(!event?.line && cartItemsEl && typeof cartItemsEl.enableLoading === 'function') {
        cartItemsEl.enableLoading(1);
      }
      
      let cartItems = [];
      if((event?.cartData)) {
        const mainCartItemsKey = Object.keys(event?.cartData?.sections).find(key => {
          return key.includes('cart-items');
        })
        const cartText = event?.cartData?.sections[mainCartItemsKey];
        const cartHtml = new DOMParser().parseFromString(cartText, 'text/html');
        cartItems = Array.from(cartHtml.querySelectorAll('.cart-item'));
      } else {
        const cart = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
        const cartText = await cart.text();
        const cartHtml = new DOMParser().parseFromString(cartText, 'text/html');
        cartItems = Array.from(cartHtml.querySelectorAll('.cart-item'));
      }
      // Build variant-in-cart map
      const variantInCart = {};
      cartItems.forEach(item => {
        const variantId = extractVariantIdFromGID(item.dataset.variantGid);
        const quantity = parseInt(item.dataset.quantity || "0");
        if (variantId) {
          variantInCart[variantId] = { quantity };
        }
      });

      let cartModified = false;
      let cartDebugReasons = [];

      // Check if there are any products that need fees
      const hasProductsWithFees = cartItems.some(item => {
        const hasAdditionalFee = !!(item.dataset.productAdditionalFee && item.dataset.productAdditionalFee !== "null");
        const hasCraftingFee = !!(item.dataset.productCraftingFee && item.dataset.productCraftingFee !== "null");
        const hasManPowerFee = !!(item.dataset.productManPower && item.dataset.productManPower !== "null");
        const isFeeItem = item.dataset.isFee === 'true';
        return (hasAdditionalFee || hasCraftingFee || hasManPowerFee) && !isFeeItem;
      });

      if (!hasProductsWithFees) {
        // No products with fees, remove all fee items using batch update
        console.log(
          "%c [V3] No products with fees - removing all fee items:",
          "background: #ef4444; color: white; font-size: 14px; padding: 3px 12px; border-radius: 4px;"
        );

        const updates = {};
        let hasFeeItems = false;

        // Collect all fee variants to remove
        cartItems.forEach(item => {
          const isFeeItem = item.dataset.isFee === 'true';
          if (isFeeItem) {
            const variantId = extractVariantIdFromGID(item.dataset.variantGid);
            updates[variantId] = 0;
            hasFeeItems = true;
            cartDebugReasons.push(`Removed fee item: ${variantId}`);
          }
        });

        // Also remove grouped fee if it exists
        if (variantInCart[GROUPED_FEE_CONFIG.variantId]?.quantity > 0) {
          updates[GROUPED_FEE_CONFIG.variantId] = 0;
          hasFeeItems = true;
          cartDebugReasons.push(`Removed grouped fee: ${GROUPED_FEE_CONFIG.variantId}`);
        }

        if (hasFeeItems) {
          // Batch remove all fees and clear attributes
          updateResponse = await updateCartWithAttributes(updates, {}, 0);
          cartModified = true;
        }
      } else {
        // Calculate total fee amount
        const { totalFeeAmount, craftingFeeAmount, feeBreakdown } = calculateTotalFeeAmount(cartItems);
        

        // Prepare batch updates
        const updates = {};

        // 1. Remove existing individual fee items (except grouped fee and crafting fees)
        cartItems.forEach(item => {
          const isFeeItem = item.dataset.isFee === 'true';
          if (isFeeItem) {
            const variantId = extractVariantIdFromGID(item.dataset.variantGid);
            
            // Check if this is a crafting fee variant
            const isCraftingFeeVariant = feeBreakdown.craftingFeeVariants.some(
              cf => cf.variantId === variantId
            );
            
            // Remove if it's not grouped fee and not a needed crafting fee
            if (variantId !== GROUPED_FEE_CONFIG.variantId && !isCraftingFeeVariant) {
              updates[variantId] = 0;
              cartDebugReasons.push(`Removed individual fee: ${variantId}`);
            }
          }
        });

        // 2. Set crafting fee quantities
        feeBreakdown.craftingFeeVariants.forEach(craftingFee => {
          const currentCraftingQty = variantInCart[craftingFee.variantId]?.quantity || 0;
          
          if (craftingFee.requiredQuantity !== currentCraftingQty) {
            updates[craftingFee.variantId] = craftingFee.requiredQuantity;
            cartDebugReasons.push(
              `Updated crafting fee: ${craftingFee.variantId} (${currentCraftingQty} → ${craftingFee.requiredQuantity})`
            );
          }
        });

        // 3. Set grouped fee quantity (always 1 if there are fees, 0 if not)
        const requiredGroupedFeeQty = totalFeeAmount > 0 ? 1 : 0;
        const currentGroupedFeeQty = variantInCart[GROUPED_FEE_CONFIG.variantId]?.quantity || 0;

        if (requiredGroupedFeeQty !== currentGroupedFeeQty) {
          updates[GROUPED_FEE_CONFIG.variantId] = requiredGroupedFeeQty;
          cartDebugReasons.push(
            `Updated grouped fee: ${GROUPED_FEE_CONFIG.variantId} (${currentGroupedFeeQty} → ${requiredGroupedFeeQty})`
          );
        }

        // 4. Execute batch update if there are changes
        if (Object.keys(updates).length > 0) {

          updateResponse = await updateCartWithAttributes(updates, feeBreakdown, totalFeeAmount);
          cartModified = true;
        } else {
          // No quantity changes, but maybe fee amounts changed - update attributes only
          const currentCart = await fetch('/cart.js');
          const currentCartData = await currentCart.json();
          
          let needsAttributeUpdate = false;
          
          // Check if fee amount changed
          if (currentCartData.attributes) {
            const currentFeeAmount = parseFloat(currentCartData.attributes.fee_total_amount || '0');
            if (currentFeeAmount !== totalFeeAmount) {
              needsAttributeUpdate = true;
            }
          } else if (totalFeeAmount > 0) {
            needsAttributeUpdate = true;
          }
          
          if (needsAttributeUpdate) {
            updateResponse = await updateCartWithAttributes({}, feeBreakdown, totalFeeAmount);
            cartModified = true;
            cartDebugReasons.push(`Updated fee attributes (amount changed)`);
          }
        }
        
      }

      if (cartModified) {
        console.log(
          "%c [V3] Cart updated with optimized fee logic:",
          "background: orange; color: white; font-size: 13px; padding: 2px 10px; border-radius: 3px;"
        );
        console.log("Debug reasons:", cartDebugReasons);

        console.log("updateResponse", updateResponse);
        
        // Update DOM
        // const updatedCart = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
        // const updatedText = await updatedCart.text();
        // const updatedHtml = new DOMParser().parseFromString(updatedText, 'text/html');
        // const newCartSection = updatedHtml.querySelector('cart-items');

        const mainCartItemsKey = Object.keys(updateResponse.sections).find(key => {
          return key.includes('cart-items');
        })
        const cartText = updateResponse.sections[mainCartItemsKey];
        const cartHtml = new DOMParser().parseFromString(cartText, 'text/html');
        const newCartSection = cartHtml.querySelector('cart-items');
        console.log("newCartSection", newCartSection);
        if (newCartSection) {
          const existingCartSection = document.querySelector('cart-items');
          if (existingCartSection) {
            existingCartSection.innerHTML = newCartSection.innerHTML;
          }
        }
      }

      if (cartItemsEl && typeof cartItemsEl.disableLoading === 'function') {
        if(event?.line) {
          cartItemsEl.disableLoading(event.line);
        } else {
          cartItemsEl.disableLoading(1);
        }
      }

      // Reset retry count on successful execution
      retryCount = 0;

    } catch (error) {
      console.error('[V3] Error in onCartUpdate:', error);
      
      // Retry logic for initial load failures
      if (!event?.source && retryCount < MAX_RETRIES) {
        retryCount++;
        console.log(
          `%c [V3] Retrying initial load (${retryCount}/${MAX_RETRIES})`,
          "background: #f59e0b; color: white; font-size: 12px; padding: 2px 8px; border-radius: 3px;"
        );
        
        // Exponential backoff with additional mobile delay
        const delay = (Math.pow(2, retryCount) * 1000) + (isMobile ? MOBILE_DELAY : 0);
        setTimeout(() => {
          isProcessing = false; // Reset flag before retry
          onCartUpdate(event);
        }, delay);
        return; // Don't reset isProcessing flag yet
      }
    } finally {
      // Always reset the flag
      isProcessing = false;
    }
  };

  // Enhanced initial load with proper sequencing
  async function initializeCartFees() {
    try {

      // 1. Validate config first
      if (!validateConfig()) {
        console.error('[V3] Invalid configuration, skipping initialization');
        return;
      }

      // 2. Wait for cart-items element to be ready
      await waitForCartItemsElement();

      // 3. Add additional delay for mobile devices
      if (isMobile) {
        await new Promise(resolve => setTimeout(resolve, MOBILE_DELAY));
      }

      // 4. Execute initial cart update
      if (!isProcessing) {
        console.log(
          "%c [V3] Executing initial cart update",
          "background: #059669; color: white; font-size: 12px; padding: 2px 8px; border-radius: 3px;"
        );
        await onCartUpdate();
      }

    } catch (error) {
      console.error('[V3] Error during initialization:', error);
    }
  }

  // Start initialization
  initializeCartFees();


  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // Prevent processing if we're already processing or if this is our own event
    if (isProcessing || event?.source === 'shipping-logic-improved-v3') {
      return;
    }
    
    console.log(
      "%c [V3] cartUpdate event triggered ",
      "background: #0070f3; color: white; font-size: 14px; padding: 3px 12px; border-radius: 4px;"
    );
    console.log("event", event);
    onCartUpdate(event);
  });
}); 

const onCartUpdateAdjustSubtotal = async () => {
  let data = await fetch(`${routes.cart_url}`);
  let html = await data.text();
  const cartHtml = new DOMParser().parseFromString(html, 'text/html');
  const subtotalHTML = cartHtml.querySelector('.totals__subtotal-value');
  document.querySelector('.totals__subtotal-value').innerHTML = subtotalHTML.innerHTML;
} 

subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  onCartUpdateAdjustSubtotal();
}) 